/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import {
  Stack,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  fetchBannerDetails,
  fetchDeleteBannerDetails,
} from "../../Services/redux/banner-upload/bannerUploadActions";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DownloadIcon from "@mui/icons-material/Download";
import PopupDialog from "../PopupDialog";
import "./ApplicationDetailsFeatureImage.css";
import { getConfig } from "../../config";
import { NoDataPlaceholder } from "../ApplicationDetailsAdditionalDocument/ApplicationDetailsAdditionalDocument";

// Constants
const FEATURE_IMAGE_GUIDELINES = "JPEG, JPE or PNG-24(without alpha channel). width 1024px . height 500px.";
const IMAGE_DIMENSIONS = { width: 1024, height: 500 };
const DIMENSION_ERROR_MSG = "Image dimensions must be 1024x500";
const ALLOWED_IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "jpe", "png"]);
const ALLOWED_IMAGE_MIME_TYPES = new Set(["image/jpeg", "image/png"]);

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Failed to read image"));
    reader.readAsDataURL(file);
  });

const loadImageFromDataUrl = (dataUrl) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = dataUrl;
  });

const validateImageSize = async (image) => {
  const dataUrl = await readFileAsDataUrl(image);
  const loadedImage = await loadImageFromDataUrl(dataUrl);

  if (
    loadedImage.width !== IMAGE_DIMENSIONS.width ||
    loadedImage.height !== IMAGE_DIMENSIONS.height
  ) {
    throw new Error(DIMENSION_ERROR_MSG);
  }

  return image;
};

const filterValidImageTypes = (images, onInvalidType) => {
  const validImages = images.filter((image) => {
    const extension = image?.name?.split(".").pop()?.toLowerCase();
    return (
      ALLOWED_IMAGE_EXTENSIONS.has(extension) ||
      ALLOWED_IMAGE_MIME_TYPES.has(image?.type)
    );
  });

  if (validImages.length !== images.length) {
    onInvalidType("Only JPEG, JPE or PNG-24 files are allowed");
  }

  return validImages;
};

// Helper: Get file extension from URL
const getFileExtension = (url, allowedExtensions = ["jpg", "jpeg", "png", "gif", "webp"], defaultExtension = "jpg") => {
  const extension = url.split(".").pop().split("?")[0].toLowerCase();
  return allowedExtensions.includes(extension) ? extension : defaultExtension;
};

// Helper: Download image file
const downloadImage = async (url, filename) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const downloadUrl = globalThis.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = `${filename}.${getFileExtension(url, ["jpg", "jpeg", "png", "gif", "webp"])}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    globalThis.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Download failed:", error);
    globalThis.open(url, "_blank");
  }
};

// Helper function to calculate effective language for OEM admin
const calculateEffectiveLanguage = (currentLanguage, submissionData) => {
  if (currentLanguage && currentLanguage !== "") {
    return currentLanguage;
  }
  const sessionLang = sessionStorage.getItem("currentLanguage");
  if (sessionLang && sessionLang !== "") {
    return sessionLang;
  }
  const defaultLang = submissionData?.defaultLanguage || submissionData?.defaultLang;
  if (defaultLang) {
    return defaultLang;
  }
  if (submissionData?.descriptions && !Array.isArray(submissionData.descriptions)) {
    const keys = Object.keys(submissionData.descriptions);
    if (keys.length > 0) {
      return keys[0];
    }
  }
  return null;
};

// Helper function to get banner URL based on portal type
const getBannerUrl = (banners) => {
  if (getConfig().REACT_APP_PORTAL === "oem-admin") {
    return banners.location;
  }
  return banners.url;
};

const getBannerForArrayDescriptions = (descriptions, selectedLanguage) => {
  const matchingDescription = descriptions.find(
    (data) => data.language == selectedLanguage
  );
  const banner = matchingDescription?.banner;
  return banner && Object.keys(banner).length > 0 ? { ...banner } : [];
};

const getBannerForOemAdminDescriptions = (descriptions, effectiveLanguage) => {
  if (!effectiveLanguage) {
    return null;
  }

  const selectedDescription = descriptions[effectiveLanguage];
  if (
    selectedDescription &&
    Object.keys(selectedDescription).length > 0 &&
    selectedDescription.banner &&
    Object.keys(selectedDescription.banner).length > 0
  ) {
    return selectedDescription.banner;
  }

  return null;
};

// Helper function to check if banner exists
const hasBanner = (banners) => {
  return (
    banners !== null &&
    Object.keys(banners).length > 0 &&
    ((getConfig().REACT_APP_PORTAL === "oem-admin" && banners.location !== "") ||
      ((getConfig().REACT_APP_PORTAL === "seller" ||
        getConfig().REACT_APP_PORTAL === "global-admin" ||
        getConfig().REACT_APP_PORTAL === "domain-approver") &&
        banners.url !== ""))
  );
};

// Component for feature image guidelines text
const FeatureImageGuidelines = () => (
  <div>
    <p className="text-feature-guidelines">{FEATURE_IMAGE_GUIDELINES}</p>
  </div>
);

function ApplicationDetailsFeatureImage() {
  const dispatch = useDispatch();
  const { appId } = useParams();
  const [dimensionError, setDimensionError] = useState("");
  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [componentName, setComponentName] = useState("");
  const [headerMessage, setHeaderMessage] = useState("");
  const submissionData = useSelector(
    (state) => state.submissionDetails.submissionDetails
  );
  const currentLanguage = useSelector(
    (state) => state.languageDetails.currentLanguage
  );
  const bannerData = useSelector((state) => state.bannerDetails);
  const [banners, setBanners] = useState({});

  const handleCloseDialog = () => {
    setOpenErrorPopup(false);
  };

  useEffect(() => {
    const effectiveLang = calculateEffectiveLanguage(currentLanguage, submissionData);
    const portal = getConfig().REACT_APP_PORTAL;

    if (portal === "oem-admin" && !Array.isArray(submissionData.descriptions)) {
      const oemBanner = getBannerForOemAdminDescriptions(
        submissionData.descriptions,
        effectiveLang
      );
      if (oemBanner) {
        setBanners(oemBanner);
      }
      return;
    }

    if (Array.isArray(submissionData.descriptions)) {
      setBanners(
        getBannerForArrayDescriptions(submissionData.descriptions, currentLanguage)
      );
    }
  }, [currentLanguage, submissionData]);

  const fileInputRef = useRef(null);

  const showErrorPopup = (errorMsg) => {
    setDimensionError(errorMsg);
    setHeaderMessage(errorMsg);
    setComponentName("appError");
    setOpenErrorPopup(true);
  };

  const validateAndProcessImages = (selectedImages) => {
    const promises = selectedImages.map(async (image) => {
      try {
        return await validateImageSize(image);
      } catch (error) {
        if (error.message === DIMENSION_ERROR_MSG) {
          showErrorPopup(DIMENSION_ERROR_MSG);
        }
        throw error;
      }
    });

    return Promise.allSettled(promises)
      .then((results) => {
        const validImages = results
          .filter((result) => result.status === "fulfilled")
          .map((result) => result.value);
        return validImages;
      })
      .catch((error) => {
        console.error("Image dimension error:", error);
        return [];
      });
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const selectedImages = Array.from(event.dataTransfer.files);
    const validTypeImages = filterValidImageTypes(selectedImages, showErrorPopup);
    if (validTypeImages.length === 0) {
      return;
    }

    validateAndProcessImages(validTypeImages).then((validImages) => {
      if (validImages.length > 0) {
        handleUpload(validImages);
      }
    });
  };

  const handleImageChange = (event) => {
    const selectedImages = Array.from(event.target.files);
    const validTypeImages = filterValidImageTypes(selectedImages, showErrorPopup);
    if (validTypeImages.length === 0) {
      return;
    }

    validateAndProcessImages(validTypeImages).then((validImages) => {
      if (validImages.length > 0) {
        handleUpload(validImages);
      }
    });
  };

  const handleUpload = (valimdImages) => {
    valimdImages.forEach((image) => {
      dispatch(
        fetchBannerDetails(appId, submissionData.id, currentLanguage, image)
      );
    });
  };

  useEffect(() => {
    if (bannerData.banners && Object.keys(bannerData.banners).length > 0) {
      setBanners(bannerData.banners);
    }
  }, [bannerData]);

  const handleRemoveImage = (bannerId) => {
    dispatch(
      fetchDeleteBannerDetails(
        appId,
        submissionData.id,
        bannerId,
        currentLanguage
      )
    );
    setBanners({});
  };

  const handleClick = () => {
    fileInputRef.current.click();
  };

  return (
    <div>
      {submissionData &&
      Object.keys(submissionData).length > 0 &&
      hasBanner(banners) ? (
        <div>
          {bannerData.loading ? (
            <Backdrop
              sx={(theme) => ({
                color: "#fff",
                zIndex: theme.zIndex.drawer + 1,
              })}
              open={bannerData.loading}
            >
              <CircularProgress color="inherit" />
            </Backdrop>
          ) : (
            !bannerData.loading && (
              <>
                <FeatureImageGuidelines />
                <div className="image-card">
                  <div className="image-preview">
                    <img
                      className="screenshot-image"
                      src={getBannerUrl(banners)}
                      alt={
                        getConfig().REACT_APP_PORTAL === "oem-admin"
                          ? "banner"
                          : `${banners.fileName}`
                      }
                      width="100%"
                    />
                  </div>
                  <div className="image-lower">
                    <div className="vertical-dots">
                      <MoreVertIcon style={{ color: "#12141599" }} />
                      <MoreVertIcon
                        style={{ color: "#12141599", marginLeft: "-17px" }}
                      />
                    </div>
                    <div className="delete-download">
                      <button
                        onClick={() =>
                          downloadImage(
                            getBannerUrl(banners),
                            `feature-image-banner` 
                          )
                        }
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: 0,
                        }}
                        aria-label="Download feature image"
                      >
                        <DownloadIcon style={{ color: "#12141599" }} />
                      </button>

                      {submissionData.status === "DRAFT" && (
                        <button
                          className="delete-button-wrap"
                          onClick={() => handleRemoveImage(banners.id)}
                        >
                          <DeleteIcon
                            style={{
                              color: "#12141599",
                            }}
                          />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )
          )}
        </div>
      ) : (
        submissionData &&
        Object.keys(submissionData).length > 0 &&
        submissionData.status !== "DRAFT" && (
          <div>
            <FeatureImageGuidelines />
            <NoDataPlaceholder />
          </div>
        )
      )}
      {submissionData &&
        Object.keys(submissionData).length > 0 &&
        submissionData.status === "DRAFT" &&
        banners &&
        Object.keys(banners).length === 0 && (
          <div>
            <FeatureImageGuidelines />
            <div>
              <Stack direction="row" alignItems="center" spacing={2}>
                <button
                  type="button"
                  className="upload-button-feature"
                  style={{
                    border: "2px dashed  rgba(18, 20, 21, 0.2)",
                    width: "100%",
                    textAlign: "center",
                    cursor: "pointer",
                    height: "60px !important",
                    background: "transparent",
                  }}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onClick={handleClick}
                >
                  <input
                    type="file"
                    multiple
                    ref={fileInputRef}
                    onChange={handleImageChange}
                    accept=".jpg,.jpeg,.jpe,.png,image/jpeg,image/png"
                    style={{ display: "none" }}
                  />
                  <p className="text-button-feature-guidelines">
                    Drag and drop images here, or click to browse
                  </p>
                </button>
              </Stack>
            </div>
          </div>
        )}
      {dimensionError && dimensionError !== "" && (
        <PopupDialog
          onClose={handleCloseDialog}
          openErrorPopup={openErrorPopup}
          headerMessage={headerMessage}
          componentName={componentName}
        />
      )}
    </div>
  );
}

export default ApplicationDetailsFeatureImage;
